import React from 'react';
import { useCart } from '../context/CartContext';
import logoImage from '../assets/logo.jpg';

const Navigation = ({ onCartClick, isMobileMenuOpen, onMobileMenuToggle, onMobileMenuClose }) => {
  const { getTotalItems } = useCart();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    onMobileMenuClose();
  };

  return (
    <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 py-0 border-b border-[#F8E8D2]/20 transition-all duration-300">
      {/* Top info bar */}
      <div className="w-full bg-primary text-gray-800 text-[10px] sm:text-xs md:text-sm text-center py-0">
        We Accept Pre-Orders Only - Book 1-2 Days in Advance
      </div>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-5 py-2 sm:py-3 md:py-4 flex justify-between items-center w-full">
        {/* Logo and Brand Name */}
        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0 flex-1 max-w-[60%]">
          <img 
            src={logoImage} 
            alt="Vanilla Sky Bakehouse Logo" 
            className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full object-cover shadow-md flex-shrink-0"
          />
          <div className="text-sm sm:text-xs md:text-base lg:text-lg xl:text-xl 2xl:text-2xl font-segoe font-bold text-gray-800 whitespace-nowrap overflow-hidden ">
            Vanilla Sky Bakehouse
          </div>
        </div>
        
        {/* Desktop Navigation Menu */}
        <ul className="hidden xl:flex space-x-6 2xl:space-x-8 3xl:space-x-10 flex-shrink-0 mr-4 2xl:mr-6">
          <li>
            <button 
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-primary font-medium transition-colors duration-300 relative after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-primary after:to-secondary after:transition-all after:duration-300 hover:after:w-full text-sm lg:text-base"
            >
              Home
            </button>
          </li>
          <li>
            <button 
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-primary font-medium transition-colors duration-300 relative after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-primary after:to-secondary after:transition-all after:duration-300 hover:after:w-full text-sm lg:text-base"
            >
              About
            </button>
          </li>
          <li>
            <button 
              onClick={() => scrollToSection('menu')}
              className="text-gray-700 hover:text-primary font-medium transition-colors duration-300 relative after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-primary after:to-secondary after:transition-all after:duration-300 hover:after:w-full text-sm lg:text-base"
            >
              Menu
            </button>
          </li>
          <li>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-primary font-medium transition-colors duration-300 relative after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-primary after:to-secondary after:transition-all after:duration-300 hover:after:w-full text-sm lg:text-base"
            >
              Contact
            </button>
          </li>
        </ul>
        
        {/* Cart and Menu Icons */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 flex-shrink-0">
          <button 
            onClick={onCartClick}
            className="cart-btn relative bg-gradient-to-br from-primary to-primary-light text-gray-700 rounded-full w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 xl:w-11 xl:h-11 flex items-center justify-center cursor-pointer transition-all duration-300 hover:-translate-y-1 shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-shopping-cart text-xs sm:text-sm md:text-base"></i>
            <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 md:-top-1.5 md:-right-1.5 lg:-top-2 lg:-right-2 bg-accent text-white rounded-full w-3 h-3 sm:w-4 sm:h-4 md:w-4 md:h-4 lg:w-5 lg:h-5 flex items-center justify-center text-xs font-semibold">
              {getTotalItems()}
            </span>
          </button>
          
          {/* Hamburger Menu for Mobile/Tablet */}
          <div 
            className={`hamburger xl:hidden flex flex-col cursor-pointer ml-1 ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={onMobileMenuToggle}
          >
            <span className="bar w-4 h-0.5 sm:w-5 sm:h-0.5 md:w-6 md:h-0.5 bg-gray-700 mb-1 transition-all duration-300"></span>
            <span className="bar w-4 h-0.5 sm:w-5 sm:h-0.5 md:w-6 md:h-0.5 bg-gray-700 mb-1 transition-all duration-300"></span>
            <span className="bar w-4 h-0.5 sm:w-5 sm:h-0.5 md:w-6 md:h-0.5 bg-gray-700 transition-all duration-300"></span>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <div className={`mobile-menu xl:hidden ${isMobileMenuOpen ? 'active' : ''}`}>
        <ul>
          <li>
            <button onClick={() => scrollToSection('home')} className="w-full text-left">
              Home
            </button>
          </li>
          <li>
            <button onClick={() => scrollToSection('about')} className="w-full text-left">
              About
            </button>
          </li>
          <li>
            <button onClick={() => scrollToSection('menu')} className="w-full text-left">
              Menu
            </button>
          </li>
          <li>
            <button onClick={() => scrollToSection('contact')} className="w-full text-left">
              Contact
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navigation; 